import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import StartPrintDialog from '../../StartPrintDialog';
import { showToastError } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { useRestoredPrintPackage } from '../../hooks/usePackages';
import { useDeleteDraftPrintRun, usePrintRunById } from '../../hooks/usePrintRuns';

export default function EditPrintContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { currentUser, org } = useLegacyAppSession();
  const organizationId = org?.id == null ? undefined : Number(org.id);
  const { deleteDraft, isDeletingDraft } = useDeleteDraftPrintRun(organizationId);
  const printRunId = String(params.printRunId || '');
  const { data: job, isLoading, error, retry: retryRun } = usePrintRunById(org?.id, printRunId, {
    initialData: location.state?.job || null,
    requireAuthoritative: true,
  });
  const {
    data: initialPackage,
    isLoading: isPackageLoading,
    error: packageError,
    retry: retryPackage,
  } = useRestoredPrintPackage(org?.id, { printRun: job });

  useEffect(() => {
    if (error) {
      showToastError('Print run could not be loaded.');
    }
  }, [error]);

  if (!printRunId) {
    return <Navigate to="/prints" replace />;
  }

  if (isLoading || isPackageLoading) {
    return null;
  }

  // Without authoritative package data the dialog would reconstruct a partial package from the run.
  if (error || packageError) {
    return (
      <div role="alert" data-testid="edit-print-package-error">
        <p>{error ? 'Print run could not be loaded.' : 'Package could not be loaded.'}</p>
        <button type="button" onClick={error || ((job?.status === 4 || job?.status === 'Draft') && job?.packageId == null) ? retryRun : retryPackage}>Retry</button>
        <button type="button" onClick={() => navigate('/prints', { replace: true })}>
          Back to Print Runs
        </button>
      </div>
    );
  }

  if (!job) {
    return <Navigate to="/prints" replace />;
  }

  const isDraft = job.status === 4 || job.status === 'Draft';
  const stagedCoaId = Number(job.stagePackageCoaId);
  const hasStagedCoaId = Number.isSafeInteger(stagedCoaId) && stagedCoaId > 0;
  const isStaged = job.packageId == null && Boolean(job.packageLabel);

  return (
    <StartPrintDialog
      defaultJobId={job.jobId ? String(job.jobId) : ''}
      readOnlyJobId={!isDraft}
      initialPrintRun={job}
      initialPackage={initialPackage}
      initialValues={{
        name: job.jobName || job.name || '',
        jobId: job.jobId ? String(job.jobId) : undefined,
        printRunId: job.id == null ? undefined : Number(job.id),
        templateId: job.templateId == null ? undefined : Number(job.templateId),
        packageId: job.packageId,
        packageTag: job.packageLabel,
        isStaged,
        stagedCoaId: hasStagedCoaId ? stagedCoaId : undefined,
        stagedCoaData: (initialPackage as { stagedCoaData?: string | null } | null)?.stagedCoaData,
        productName: job.productOverrideName || job.productName || job.product || '',
        count: job.count ?? 1,
        packagedDate: job.packagedDate || job.packDate || '',
        printedDate: isDraft
          ? ''
          : (job.printedDate
              ? new Date(job.printedDate).toLocaleString()
              : job.printDate || ''),
        status: job.status,
      }}
      onClose={() => navigate('/prints', { replace: true })}
      onSave={() => navigate('/prints', { replace: true })}
      onDeleteDraft={deleteDraft}
      isDeletingDraft={isDeletingDraft}
      organizationId={organizationId}
      currentUser={currentUser}
      existingPrintRun={job}
    />
  );
}
